import React, { ReactNode } from 'react';
import { TTransactionGeneralLedgerCode } from '@/context/types/Business';
import useHomeContext from '@/hooks/useHomeContext';
import SelectGeneralLedgerCode from '../SelectGeneralLedgerCode';
import AvatarIcon from '@/components/AvatarIcon';
import CurrencyFormat from 'react-currency-format';
import Capsule from '@/components/Capsule';
import { returnTypeofGetTransactionStatus } from '../../utils/getTransactionStatus';
import { generateOption } from '../../utils/generateGLCodeOption';
import useGeneralLedgerCode from '../../hooks/useGeneralLedgerCode';

export const renderDetails = <
  T extends {
    title: string;
    data: string | Record<string, string | undefined | null> | number | undefined | null;
  }
>(
  detail: T,
  index: number,
  transactionId: string
): ReactNode => {
  const {
    originalTransactionGeneralLedgerCodes,
    archivedTransactionGeneralLedgerCodes,
    setGeneralLedgerCodeSearchInput,
    generalLedgerCodeSearchInput,
    transactionGeneralLedgerCodes
  } = useHomeContext()!;
  const { handleOnOptionSelected, handleOnOptionRemoved } = useGeneralLedgerCode();

  if (
    (typeof detail.data === 'object' || detail.data === undefined) &&
    detail.title === 'GL code' &&
    ((originalTransactionGeneralLedgerCodes && originalTransactionGeneralLedgerCodes.length > 0) ||
      detail.data !== undefined)
  ) {
    const currentCodeId = detail.data ? detail.data.id! : null;

    let codeValue;

    if (detail.data) {
      codeValue = originalTransactionGeneralLedgerCodes?.find(
        (code) => code.code === (detail.data as TTransactionGeneralLedgerCode).code
      );

      // Check for archived codes
      if (codeValue === undefined) {
        codeValue = archivedTransactionGeneralLedgerCodes?.find(
          (code) => code.code === (detail.data as TTransactionGeneralLedgerCode).code
        );
      }
    }

    return (
      <tr className="odd:bg-white even:bg-emerge-base-3" key={`detail-${index}`}>
        <th
          scope="row"
          className="px-6 py-4 font-normal text-emerge-navy whitespace-nowrap rounded-l-lg">
          {detail.title}
        </th>
        <td className="px-6 py-4 pr-0 text-left rounded-r-lg">
          <div className="flex items-center gap-x-1 justify-end">
            <SelectGeneralLedgerCode
              className="w-full rounded-lg bg-emerge-base-3 p-2 xl:w-[15.5rem] 2xl:w-[18.5rem]"
              value={
                codeValue
                  ? `${codeValue.code} - ${codeValue.name}`
                  : detail.data
                    ? `${detail.data?.code} - ${detail.data?.name}`
                    : 'Assign GL code'
              }
              selectedColor={
                codeValue ? codeValue.color : detail.data?.color ? detail.data.color : ''
              }
              options={transactionGeneralLedgerCodes!.map(generateOption)}
              optionsContainerClass="w-full max-h-[21rem] rounded-lg"
              onOptionSelected={(option) =>
                handleOnOptionSelected(option, transactionId, currentCodeId)
              }
              onOptionRemoved={() => handleOnOptionRemoved(transactionId)}
              onTextChange={(e) => setGeneralLedgerCodeSearchInput!(e.target.value)}
              search={generalLedgerCodeSearchInput!}
              clearSearch={() => setGeneralLedgerCodeSearchInput!('')}
              optionSelected={detail.data !== undefined}
            />
          </div>
        </td>
      </tr>
    );
  } else if (typeof detail.data === 'object' && detail.title === 'Team member') {
    return (
      <tr className="odd:bg-white even:bg-emerge-base-3" key={`detail-${index}`}>
        <th
          scope="row"
          className="px-6 py-4 font-normal text-emerge-navy whitespace-nowrap rounded-l-lg">
          {detail.title}
        </th>
        <td className="px-6 py-4 text-right rounded-r-lg">
          {detail.data && detail.data.name ? (
            <div className="flex items-center gap-x-1 justify-end">
              <AvatarIcon
                key={`transaction-details-${detail.title}-${index}`}
                name={detail.data?.name ?? ''}
                imgSrc={detail.data?.avatarUrl}
                className="m-0"
              />
              {detail.data.name}
            </div>
          ) : (
            ''
          )}
        </td>
      </tr>
    );
  } else if (typeof detail.data === 'string' && returnTypeofGetTransactionStatus(detail.data)) {
    return (
      <tr className="odd:bg-white even:bg-emerge-base-3" key={`detail-${index}`}>
        <th
          scope="row"
          className="px-6 py-4 font-normal text-emerge-navy whitespace-nowrap rounded-l-lg">
          {detail.title}
        </th>
        <td className="px-6 py-4 text-right rounded-r-lg">
          <div className="flex items-center gap-x-1 justify-end">
            <Capsule variant={detail.data} />
          </div>
        </td>
      </tr>
    );
  } else if (typeof detail.data === 'string') {
    return (
      <tr className="odd:bg-white even:bg-emerge-base-3" key={`detail-${index}`}>
        <th
          scope="row"
          className="px-6 py-4 font-normal text-emerge-navy whitespace-nowrap rounded-l-lg">
          {detail.title}
        </th>
        <td className="px-6 py-4 text-right rounded-r-lg">{detail.data}</td>
      </tr>
    );
  } else if (typeof detail.data === 'number') {
    return (
      <tr className="odd:bg-white even:bg-emerge-base-3" key={`detail-${index}`}>
        <th
          scope="row"
          className="px-6 py-4 font-normal text-emerge-navy whitespace-nowrap rounded-l-lg">
          {detail.title}
        </th>
        <td className="px-6 py-4 text-right rounded-r-lg">
          <CurrencyFormat
            className="text-sm"
            value={Number(detail.data).toFixed(2)}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            decimalScale={2}
          />
        </td>
      </tr>
    );
  }
};
